import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { ngClassObject } from '../../../types';
import { IconsModule } from '../../icons/icons.module';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'p-textarea',
  templateUrl: './p-textarea.component.html',
  styleUrls: ['./p-textarea.component.scss'],
  standalone: true,
  imports: [IconsModule, CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PTextareaComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PTextareaComponent implements ControlValueAccessor {
  @Input() formControl!: FormControl;
  @Input() name!: string;
  @Input() label!: string;
  @Input() rows: number = 3; // Nombre de lignes par défaut pour le textarea
  @Input() size: 'large' | 'small' | 'default' = 'default';
  @Input() description!: string;
  @Input() autocomplete!: string;
  @Input() disabled!: boolean;
  @Input() hideRequiredStar!: boolean;
  @Input() withRightIcon = false;
  @Input() helpText!: string | undefined;
  @Input() placeholder!: string;
  @Input() tabindex!: number;
  @Input() maxLength!: number;

  // Le message d'erreur qui apparaîtra si le form control n'est pas valide.
  @Input() errorMessage!: string;

  onChanged!: (value: string) => void;
  onTouched!: () => void;
  value: string = '';

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  writeValue(value: string): void {
    if (this.formControl.value) {
      this.value = this.formControl.value;
    }

    this.value = value;
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  markAsTouched(): void {
    this.onTouched();
  }

  // Pour rendre le form control obligatoire (required),
  // il faut lui attribuer le validateur Angular Validators.required dans le composant parent.
  get required(): boolean {
    return this.formControl.hasValidator(Validators.required);
  }

  get classes(): ngClassObject<string> {
    return {
      'form-control-lg': this.size === 'large',
      'form-control-sm': this.size === 'small',
    };
  }
}
